// uiarc: button (https://uiarc.dev/r/button.json) — Base UI + Tailwind'e çevrildi, CSS Modules/Radix yok.
// Davranış uiarc ile aynı: basınca yaylı küçülme, etiket/ikon değişince bulanık çapraz geçiş ve genişliğin
// yayla uyması, `loading` (odak korunur, tıklama yutulur, dönen gösterge). `prefers-reduced-motion` altında
// hareket kalkar, yalnız kısa solma kalır.
import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'
import {
  AnimatePresence,
  animate,
  motion,
  useIsPresent,
  useMotionValue,
  useReducedMotion,
} from 'motion/react'
import type { TargetAndTransition, Variants } from 'motion/react'
import {
  isValidElement,
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react'
import { motionTokens, reducedTransition } from '@/lib/motion'
import { cn } from '@/lib/utils'

const sizes = {
  sm: 'h-(--control-height-sm) px-3',
  md: 'h-(--control-height-md)',
  lg: 'h-(--control-height-lg) px-5',
  'icon-sm': 'size-(--control-height-sm) px-0',
  'icon-md': 'size-(--control-height-md) px-0',
  'icon-lg': 'size-(--control-height-lg) px-0',
}

// Çıkış öncesi `popLayout` ile çıkan etiketin yerini koruması için `relative` şart; yükseklik `h-`, uiarc'ın
// `min-height`'ı yerine (çağıranlar `size-6` gibi sınıflarla ezebilsin; tailwind-merge aynı grup).
const buttonVariants = cva(
  "relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-control border border-transparent px-4 text-sm leading-body font-medium whitespace-nowrap select-none [-webkit-tap-highlight-color:transparent] transition-[color,background-color,border-color,opacity,box-shadow] duration-(--duration-fast) ease-standard disabled:cursor-not-allowed disabled:opacity-52 aria-busy:cursor-progress motion-reduce:transition-none [&:is([aria-haspopup]:not([aria-haspopup=false]),[data-popup-open],[role=combobox])]:transform-none! [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        primary:
          'border-foreground bg-foreground text-background not-disabled:hover:opacity-91 not-disabled:hover:shadow-resting not-disabled:active:opacity-84',
        secondary:
          'border-border bg-surface text-foreground not-disabled:hover:bg-surface-muted not-disabled:hover:shadow-resting not-disabled:active:bg-surface-muted disabled:border-border disabled:bg-surface-muted disabled:text-text-muted disabled:opacity-100',
        ghost:
          'bg-transparent text-text-secondary not-disabled:hover:bg-surface-muted not-disabled:hover:text-foreground not-disabled:active:bg-surface-muted not-disabled:active:text-foreground disabled:text-text-muted disabled:opacity-100',
        danger:
          'border-border bg-surface text-danger not-disabled:hover:border-danger not-disabled:hover:bg-surface-muted not-disabled:active:border-danger not-disabled:active:bg-surface-muted disabled:border-border disabled:bg-surface-muted disabled:text-[color-mix(in_oklab,var(--danger)_55%,var(--surface-muted))] disabled:opacity-100',
        // uiarc'ta olmayan, mevcut kullanım için korunur.
        link: 'bg-transparent text-primary underline-offset-4 not-disabled:hover:underline',
        // Eski adlar (39 çağrı yeri): uiarc karşılığına eşlenir.
        default:
          'border-foreground bg-foreground text-background not-disabled:hover:opacity-91 not-disabled:hover:shadow-resting not-disabled:active:opacity-84',
        outline:
          'border-border bg-surface text-foreground not-disabled:hover:bg-surface-muted not-disabled:hover:shadow-resting not-disabled:active:bg-surface-muted disabled:border-border disabled:bg-surface-muted disabled:text-text-muted disabled:opacity-100',
        destructive:
          'border-border bg-surface text-danger not-disabled:hover:border-danger not-disabled:hover:bg-surface-muted not-disabled:active:border-danger not-disabled:active:bg-surface-muted disabled:border-border disabled:bg-surface-muted disabled:text-[color-mix(in_oklab,var(--danger)_55%,var(--surface-muted))] disabled:opacity-100',
      },
      size: {
        sm: sizes.sm,
        md: sizes.md,
        lg: sizes.lg,
        'icon-sm': sizes['icon-sm'],
        icon: sizes['icon-md'],
        'icon-lg': sizes['icon-lg'],
        // Eski adlar: uiarc'ta `xs` yok, en küçük uiarc boyutuna (sm) eşlenir.
        default: sizes.md,
        xs: sizes.sm,
        'icon-xs': sizes['icon-sm'],
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

type ButtonProps = Omit<
  ButtonPrimitive.Props,
  'className' | 'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart'
> &
  VariantProps<typeof buttonVariants> & {
    className?: string
    /** Yükleniyor: odak korunur, tıklama yutulur, etiket yerine dönen gösterge çıkar. */
    loading?: boolean
  }

/** İkon düğmeleri biraz daha derin, geniş düğmeler biraz daha az basılır; her boy aynı itiş gibi okunur. */
const pressVariants: Variants = {
  pressed: (button: RefObject<HTMLButtonElement | null>) => {
    const width = button.current?.offsetWidth ?? 0
    return {
      scale: width > 220 ? 0.985 : width && width <= 48 ? 0.96 : 0.97,
      transition: { duration: motionTokens.duration.instant, ease: motionTokens.ease.standard },
    }
  },
}

const rest: TargetAndTransition = { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }
/** Metin ~.3em yumuşak bulanıklıktan yükselir; giden etiket biraz daha hızlı kalkar. */
const textIn: TargetAndTransition = {
  opacity: 0,
  y: 4,
  filter: `blur(${motionTokens.blur.soft}px)`,
}
const textOut: TargetAndTransition = {
  opacity: 0,
  y: -3,
  filter: `blur(${motionTokens.blur.soft}px)`,
  transition: { duration: motionTokens.duration.fast, ease: motionTokens.ease.standard },
}
const iconIn: TargetAndTransition = {
  opacity: 0,
  scale: 0.6,
  filter: `blur(${motionTokens.blur.subtle}px)`,
}
const iconOut: TargetAndTransition = {
  opacity: 0,
  scale: 0.6,
  filter: `blur(${motionTokens.blur.subtle}px)`,
  transition: { duration: motionTokens.duration.fast, ease: motionTokens.ease.standard },
}
const fadeIn: TargetAndTransition = { ...rest, opacity: 0 }
const fadeOut: TargetAndTransition = {
  opacity: 0,
  transition: { duration: motionTokens.duration.instant },
}
/** Ölçek yayla gider; opaklık ve bulanıklık tween: bulanıklık sıfırın altına taşmasın. */
const iconEnter = {
  ...motionTokens.spring.snappy,
  opacity: { duration: motionTokens.duration.fast, ease: motionTokens.ease.enter },
  filter: { duration: motionTokens.duration.fast, ease: motionTokens.ease.enter },
} as const

/** Etiket içeriği anahtarı: metin + öğe adları; yeni etiket/ikon çapraz geçer, yalnız prop değişimi yerinde kalır. */
function labelKey(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number' || typeof node === 'bigint')
    return String(node)
  if (Array.isArray(node)) return node.map(labelKey).join('')
  if (!isValidElement(node)) return ''
  const type = node.type as string | { displayName?: string; name?: string }
  const name = typeof type === 'string' ? type : (type?.displayName ?? type?.name ?? '')
  return `<${name}>${labelKey((node.props as { children?: ReactNode }).children)}`
}

/**
 * Etiket değişince kutuyu yeni etiketin doğal genişliğine yayla uydurur, boyut sıçramaz.
 * Giden etiket akıştan hemen çıkar, eski genişliği tutmaz. Diğer boyut değişimleri (geç gelen font, ebeveyn
 * yerleşimi) yeni genişliğe doğrudan atlar; ilk boyamada sallanma olmaz.
 */
function useMorphWidth(content: RefObject<HTMLElement | null>, key: string, reduced: boolean) {
  const width = useMotionValue<number | 'auto'>('auto')
  const lastKey = useRef(key)
  const armedUntil = useRef(0)
  useLayoutEffect(() => {
    if (lastKey.current === key) return
    lastKey.current = key
    armedUntil.current = performance.now() + 700
  }, [key])
  useEffect(() => {
    const node = content.current
    const slot = node?.parentElement
    if (!node || !slot || typeof ResizeObserver === 'undefined') return
    let measured = false
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      const next = entry.contentRect.width
      if (!next || !measured || reduced || performance.now() > armedUntil.current) {
        measured = next > 0
        width.jump(next || 'auto')
        delete slot.dataset.morphing
        return
      }
      slot.dataset.morphing = ''
      animate(width, next, {
        ...motionTokens.spring.morph,
        onComplete: () => {
          delete slot.dataset.morphing
        },
      })
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [content, reduced, width])
  return width
}

function LabelPhase({
  children,
  icon,
  reduced,
  ref,
}: {
  children: ReactNode
  icon: boolean
  reduced: boolean
  ref?: Ref<HTMLSpanElement>
}) {
  const present = useIsPresent()
  return (
    <motion.span
      ref={ref}
      className="inline-flex items-center justify-center gap-2 whitespace-nowrap [&_svg]:flex-none"
      aria-hidden={present ? undefined : true}
      initial={reduced ? fadeIn : icon ? iconIn : textIn}
      animate={rest}
      exit={reduced ? fadeOut : icon ? iconOut : textOut}
      transition={
        reduced
          ? reducedTransition
          : icon
            ? iconEnter
            : { duration: motionTokens.duration.standard, ease: motionTokens.ease.enter }
      }
    >
      {children}
    </motion.span>
  )
}

function Button({
  className,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  children,
  onClick,
  render,
  ref,
  ...props
}: ButtonProps) {
  const reduceMotion = useReducedMotion() ?? false
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const contentRef = useRef<HTMLSpanElement>(null)
  const key = labelKey(children)
  const width = useMorphWidth(contentRef, key, reduceMotion)

  // Menü/popover/dialog çıpası olan düğme basılıyken ölçeklenmez: katman küçülmüş çıpayı ölçmesin.
  // Base UI tetikleyicileri `aria-haspopup` verir; sınıf aynı korumayı CSS'te tekrarlar.
  const popup = props['aria-haspopup']
  const anchorsLayer =
    (popup !== undefined && popup !== false && popup !== 'false') ||
    props.role === 'combobox' ||
    (props as Record<string, unknown>)['data-popup-open'] !== undefined
  const inert =
    disabled || loading || props['aria-disabled'] === true || props['aria-disabled'] === 'true'

  const label = (
    <>
      <AnimatePresence initial={false}>
        {loading ? (
          <motion.span
            key="loader"
            className="absolute top-1/2 left-1/2 -m-2 grid size-4"
            aria-hidden="true"
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={reduceMotion ? fadeOut : { ...iconOut, scale: 0.8 }}
            transition={reduceMotion ? reducedTransition : iconEnter}
          >
            <span className="animate-[spin_.7s_linear_infinite] rounded-full border-[1.5px] border-current border-r-transparent motion-reduce:animate-none" />
          </motion.span>
        ) : null}
      </AnimatePresence>
      <motion.span
        className={cn(
          // Giden etiket kutunun kendisine karşı akıştan çıkar; kutu yeni içeriği yayla izlerken etiket ortalı kalır.
          'ease-standard relative inline-flex min-w-0 items-center justify-center transition-opacity duration-(--duration-fast) data-[morphing]:[clip-path:inset(-50%_-0.75rem)] motion-reduce:duration-(--duration-instant) motion-reduce:ease-linear',
          loading && 'opacity-0',
        )}
        style={{ width }}
      >
        <span ref={contentRef} className="inline-flex flex-none items-center">
          <AnimatePresence mode="popLayout" initial={false}>
            <LabelPhase
              key={key}
              icon={!/\S/.test(key.replace(/<[^>]*>/g, ''))}
              reduced={reduceMotion}
            >
              {children}
            </LabelPhase>
          </AnimatePresence>
        </span>
      </motion.span>
    </>
  )

  return (
    <ButtonPrimitive
      data-slot="button"
      {...props}
      ref={ref}
      disabled={disabled}
      className={cn(
        buttonVariants({ variant, size }),
        // `render` ile gelen öğede (ör. bağlantı) motion kökü yok: basma CSS ile, yine yaysız.
        render && !inert && 'active:scale-[.97] motion-reduce:active:scale-100',
        className,
      )}
      render={
        render ?? (
          <motion.button
            ref={buttonRef}
            custom={buttonRef}
            variants={pressVariants}
            whileTap={reduceMotion || anchorsLayer || inert ? undefined : 'pressed'}
            transition={motionTokens.spring.snappy}
          />
        )
      }
      aria-busy={loading || undefined}
      // Yükleniyorken düğme odaklanabilir kalır (disabled odağı düşürürdü) ve basışı yutar.
      aria-disabled={loading || props['aria-disabled'] || undefined}
      onClick={
        loading
          ? (event) => {
              event.preventDefault()
            }
          : onClick
      }
    >
      {label}
    </ButtonPrimitive>
  )
}

export { Button, buttonVariants, type ButtonProps }
